import type {TMat2D} from '@postermywall/fabricjs-2';
import {Point} from '@postermywall/fabricjs-2';
import {GUIDELINES_COLOR} from '@PosterWhiteboard/page/page.types';
import {CUSTOM_CONTROLS_WRAPPER} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import type {GuideFrame, Measurement, SnapAxis, SnapLine, SnapPoint} from './alignment-guides.types';
import {
  OVERLAY_LABEL_FONT_SIZE_PX,
  OVERLAY_LABEL_HEIGHT_PX,
  OVERLAY_LABEL_LINE_CLEARANCE_PX,
  OVERLAY_LABEL_PADDING_X_PX,
  OVERLAY_LABEL_TEXT_COLOR,
  OVERLAY_LINE_WIDTH_PX,
  OVERLAY_MEASUREMENT_TICK_PX,
  OVERLAY_OBJECT_LINE_DASH,
  SnapLineStyle,
  SVG_NAMESPACE,
} from './alignment-guides.types';

/** The gap a pill labels: which scene axis it runs along and its on-screen length. */
interface LabelGap {
  axis: SnapAxis;
  gapLengthPx: number;
}

/** The scene-to-overlay mapping for one render: the page's zoom and its scene size. */
export interface OverlayViewport {
  zoom: number;
  sceneWidth: number;
  sceneHeight: number;
}

/**
 * Draws alignment guides as an SVG instead of onto the canvas's top context: crisp 1px lines at any zoom,
 * real text in the labels. The SVG lives in the page's custom controls layer (`CUSTOM_CONTROLS_WRAPPER`, the
 * layer the selection outline and resize handles are drawn in), not in the canvas backdrop: the backdrop
 * clips to the page (`overflow: hidden`), the controls layer does not, so a line on the right or bottom
 * canvas edge and a readout to an item hanging off the page stay visible. That layer sits on the backdrop's
 * top-left, follows the page's CSS scale, and lays its children out in scene units times zoom.
 */
export class AlignmentGuidesOverlay {
  readonly #svg: SVGSVGElement;
  readonly #pageHashedId: string;

  constructor(pageHashedId: string) {
    this.#pageHashedId = pageHashedId;
    this.#svg = document.createElementNS(SVG_NAMESPACE, 'svg');
    this.#svg.setAttribute('data-alignment-guides', '');
    Object.assign(this.#svg.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      overflow: 'visible',
      pointerEvents: 'none',
    });
  }

  public render(frame: GuideFrame, viewport: OverlayViewport): void {
    this.clear();
    if (frame.lines.length === 0 && frame.measurements.length === 0) {
      return;
    }
    if (!this.#mount()) {
      return;
    }
    this.#svg.style.width = `${viewport.sceneWidth * viewport.zoom}px`;
    this.#svg.style.height = `${viewport.sceneHeight * viewport.zoom}px`;
    const sceneToOverlay: TMat2D = [viewport.zoom, 0, 0, viewport.zoom, 0, 0];
    for (const line of frame.lines) {
      this.#svg.appendChild(this.#createSnapLine(line, sceneToOverlay));
    }
    for (const measurement of frame.measurements) {
      this.#svg.appendChild(this.#createMeasurement(measurement, sceneToOverlay));
    }
  }

  /**
   * Puts the SVG into the page's controls layer. React renders that layer (portaled into the whiteboard
   * container) and can remount it, so it is looked up again whenever the SVG has fallen out of the document.
   * False while the layer is not there.
   */
  #mount(): boolean {
    if (this.#svg.isConnected) {
      return true;
    }
    const controlsLayer = document.querySelector<HTMLElement>(`.${CUSTOM_CONTROLS_WRAPPER}#${CSS.escape(this.#pageHashedId)}`);
    if (!controlsLayer) {
      return false;
    }
    controlsLayer.appendChild(this.#svg);
    return true;
  }

  public clear(): void {
    this.#svg.replaceChildren();
  }

  public dispose(): void {
    this.#svg.remove();
  }

  #createSnapLine(line: SnapLine, sceneToOverlay: TMat2D): SVGLineElement {
    const [start, end] = this.#lineEndpoints(line.axis, line.value, line.from, line.to, sceneToOverlay);
    const element = this.#createLine(start, end);
    if (line.style === SnapLineStyle.OBJECT) {
      element.setAttribute('stroke-dasharray', OVERLAY_OBJECT_LINE_DASH);
    }
    return element;
  }

  #createMeasurement(measurement: Measurement, sceneToOverlay: TMat2D): SVGGElement {
    const group = document.createElementNS(SVG_NAMESPACE, 'g');
    // A gap along `x` is drawn as a horizontal line, which in SnapLine terms is a `y` line at `at`.
    const lineAxis = measurement.axis === 'x' ? 'y' : 'x';
    const [start, end] = this.#lineEndpoints(lineAxis, measurement.at, measurement.from, measurement.to, sceneToOverlay);
    group.appendChild(this.#createLine(start, end));
    group.appendChild(this.#createTick(start, measurement.axis));
    group.appendChild(this.#createTick(end, measurement.axis));
    const gapLengthPx = measurement.axis === 'x' ? Math.abs(end.x - start.x) : Math.abs(end.y - start.y);
    group.appendChild(this.#createLabel(measurement.label, {x: (start.x + end.x) / 2, y: (start.y + end.y) / 2}, {axis: measurement.axis, gapLengthPx}));
    return group;
  }

  /** Screen-space endpoints of a line at `value` on `axis`, spanning `from`..`to` on the other axis, pixel-snapped so 1px strokes stay crisp. */
  #lineEndpoints(axis: 'x' | 'y', value: number, from: number, to: number, sceneToOverlay: TMat2D): [SnapPoint, SnapPoint] {
    const start = axis === 'x' ? {x: value, y: from} : {x: from, y: value};
    const end = axis === 'x' ? {x: value, y: to} : {x: to, y: value};
    return [this.#toScreen(start, sceneToOverlay), this.#toScreen(end, sceneToOverlay)];
  }

  #toScreen(point: SnapPoint, sceneToOverlay: TMat2D): SnapPoint {
    const screen = new Point(point.x, point.y).transform(sceneToOverlay);
    return {x: Math.round(screen.x) + 0.5, y: Math.round(screen.y) + 0.5};
  }

  #createLine(start: SnapPoint, end: SnapPoint): SVGLineElement {
    const line = document.createElementNS(SVG_NAMESPACE, 'line');
    line.setAttribute('x1', String(start.x));
    line.setAttribute('y1', String(start.y));
    line.setAttribute('x2', String(end.x));
    line.setAttribute('y2', String(end.y));
    line.setAttribute('stroke', GUIDELINES_COLOR);
    line.setAttribute('stroke-width', String(OVERLAY_LINE_WIDTH_PX));
    return line;
  }

  /** A short perpendicular stroke that closes a measurement line at one end. */
  #createTick(point: SnapPoint, axis: 'x' | 'y'): SVGLineElement {
    const tick = OVERLAY_MEASUREMENT_TICK_PX;
    return axis === 'x'
      ? this.#createLine({x: point.x, y: point.y - tick}, {x: point.x, y: point.y + tick})
      : this.#createLine({x: point.x - tick, y: point.y}, {x: point.x + tick, y: point.y});
  }

  #createLabel(text: string, lineCenter: SnapPoint, gap: LabelGap): SVGGElement {
    const group = document.createElementNS(SVG_NAMESPACE, 'g');
    const pill = document.createElementNS(SVG_NAMESPACE, 'rect');
    const label = document.createElementNS(SVG_NAMESPACE, 'text');
    label.textContent = text;
    label.setAttribute('fill', OVERLAY_LABEL_TEXT_COLOR);
    label.setAttribute('font-size', String(OVERLAY_LABEL_FONT_SIZE_PX));
    label.setAttribute('font-weight', '600');
    label.setAttribute('font-family', 'inherit');
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('dominant-baseline', 'central');
    pill.setAttribute('fill', GUIDELINES_COLOR);
    pill.setAttribute('rx', String(OVERLAY_LABEL_HEIGHT_PX / 2));
    pill.setAttribute('height', String(OVERLAY_LABEL_HEIGHT_PX));
    group.append(pill, label);
    // The pill is sized from the rendered text, which only has a length once it is in the document.
    this.#svg.appendChild(group);
    const width = label.getComputedTextLength() + OVERLAY_LABEL_PADDING_X_PX * 2;
    const center = this.#labelCenter(lineCenter, width, gap);
    label.setAttribute('x', String(center.x));
    label.setAttribute('y', String(center.y));
    pill.setAttribute('width', String(width));
    pill.setAttribute('x', String(center.x - width / 2));
    pill.setAttribute('y', String(center.y - OVERLAY_LABEL_HEIGHT_PX / 2));
    return group;
  }

  /**
   * The pill sits on its line while the gap is long enough to show line on both sides of it. A shorter gap
   * would vanish under the pill, so the pill moves clear of the line: below a horizontal gap, right of a vertical one.
   */
  #labelCenter(lineCenter: SnapPoint, pillWidth: number, gap: LabelGap): SnapPoint {
    const pillExtentAlongGap = gap.axis === 'x' ? pillWidth : OVERLAY_LABEL_HEIGHT_PX;
    if (gap.gapLengthPx >= pillExtentAlongGap + OVERLAY_LABEL_LINE_CLEARANCE_PX * 2) {
      return lineCenter;
    }
    const pillExtentAcrossGap = gap.axis === 'x' ? OVERLAY_LABEL_HEIGHT_PX : pillWidth;
    const offLineOffset = pillExtentAcrossGap / 2 + OVERLAY_LABEL_LINE_CLEARANCE_PX;
    return gap.axis === 'x' ? {x: lineCenter.x, y: lineCenter.y + offLineOffset} : {x: lineCenter.x + offLineOffset, y: lineCenter.y};
  }
}
